import React, { useRef, useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { MdSend } from "react-icons/md";
import useChatActions from '../hooks/useChatActions';
import { useChat } from '../context/ChatProvider';
import { useTranslation } from "react-i18next";

const MessageForm = styled.form`
    position: sticky;
    bottom: 0;
    display: flex;
    align-items: center;
    padding: 0.6rem 0.8rem;
    flex: none;
    background: rgba(255, 255, 255, 0.03);
    border-top: 1px solid rgba(255, 255, 255, 0.06);
`;

const InputContainer = styled.div`
    position: relative;
    width: 100%;
    display: flex;
    align-items: center;
    gap: 0.6rem;
`;

const TextArea = styled.textarea`
    flex: 1;
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 16px;
    background: rgba(255, 255, 255, 0.06);
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.9rem;
    line-height: 1.5;
    padding: 0.7rem 1rem;
    resize: none;
    overflow-y: auto;
    transition: border-color 0.2s, background 0.2s;
    font-family: 'Poppins', sans-serif;

    &::placeholder {
        color: rgba(255, 255, 255, 0.3);
    }

    &:focus {
        outline: none;
        border-color: rgba(99, 102, 241, 0.5);
        background: rgba(255, 255, 255, 0.08);
    }

    @media (max-width: 820px) {
        font-size: 1rem;
    }
`;

const SendButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.8em;
    height: 2.8em;
    border: none;
    border-radius: 50%;
    background: ${props => props.$active ? 'linear-gradient(135deg, #6366f1, #8b5cf6)' : 'rgba(255, 255, 255, 0.08)'};
    cursor: pointer;
    transition: all 0.2s ease;
    flex-shrink: 0;
    box-shadow: ${props => props.$active ? '0 4px 16px rgba(99, 102, 241, 0.35)' : 'none'};

    &:hover {
        background: ${props => props.$active ? 'linear-gradient(135deg, #4f46e5, #7c3aed)' : 'rgba(255, 255, 255, 0.12)'};
        transform: ${props => props.$active ? 'scale(1.05)' : 'none'};
    }

    & svg {
        fill: ${props => props.$active ? 'white' : 'rgba(255, 255, 255, 0.3)'};
        transition: fill 0.2s;
    }
`;

const ChatForm = () => {
    const inputRef = useRef(null);
    const { sendMessage } = useChatActions();
    const { currentRoom, setIsMessageSending, chatInputMode, setIsTextareaFocused, isMobile, socket, user, blockSending } = useChat();
    const { t } = useTranslation();
    const [typing, setTyping] = useState(false);
    const [hasText, setHasText] = useState(false);
    const typingTimeoutId = useRef(null);
    const sendButtonClickedRef = useRef(false);

    const handleTyping = useCallback(() => {
        if (!typing) {
            socket.emit('typing', { roomID: currentRoom.id, userID: user.id, username: user.username, avatar: user.avatar, language: user.language });
            setTyping(true);
        }
        clearTimeout(typingTimeoutId.current);
        typingTimeoutId.current = setTimeout(() => { setTyping(false); }, 3000);
    }, [typing, currentRoom.id, user.id, socket]);

    const onSubmit = (e) => {
        e.preventDefault();
        sendButtonClickedRef.current = true;
        if (blockSending) return;
        if (!inputRef.current.value) return;

        setIsMessageSending(true);
        let message = inputRef.current.value;
        inputRef.current.value = '';
        setHasText(false);
        inputRef.current.focus();

        sendMessage(message, currentRoom.id)
            .catch(error => {
                console.log(error);
                setIsMessageSending(false);
            });
    };

    useEffect(() => {
        const handleFocus = () => {
            if (isMobile) {
                setIsTextareaFocused(true);
                window.scrollTo(0, document.body.scrollHeight);
            }
        };
        const handleBlur = () => {
            if (sendButtonClickedRef.current) {
                sendButtonClickedRef.current = false;
                return;
            }
            setIsTextareaFocused(false);
            window.scrollTo(0, 0);
        };

        if (inputRef.current) {
            inputRef.current.addEventListener('focus', handleFocus);
            inputRef.current.addEventListener('blur', handleBlur);
        }
        return () => {
            if (inputRef.current) {
                inputRef.current.removeEventListener('focus', handleFocus);
                inputRef.current.removeEventListener('blur', handleBlur);
            }
        };
    }, [setIsTextareaFocused]);

    return (
        <MessageForm onSubmit={onSubmit}>
            <InputContainer>
                <TextArea
                    disabled={blockSending}
                    inputMode={chatInputMode}
                    placeholder={t('ChatFormText')}
                    ref={inputRef}
                    maxLength={250}
                    rows={1}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            onSubmit(e);
                        }
                    }}
                    onChange={(e) => {
                        handleTyping();
                        setHasText(e.target.value.length > 0);
                    }}
                />
                <SendButton $active={hasText} type="submit">
                    <MdSend size="1.2em" />
                </SendButton>
            </InputContainer>
        </MessageForm>
    );
};

export default ChatForm;
